import React, { useEffect, useMemo, useRef, useState } from 'react';
import { simulate, simClock, SCENARIOS } from './simulate';
import { deriveTelemetry, emptyTelemetry, WINDOWS } from './telemetryState';
import useCommandToggle from './useCommandToggle';

// One hook per command surface. Source is either the real feed (`fetchReal`
// returns { metrics, planeCalls, usageDays, anomalies } for a window) or the
// seeded simulator over the same roster. Both land in deriveTelemetry so the
// stage never knows which it is — the SIMULATED watermark + caption prefix
// are the only tell. Simulate is per-surface, off by default, and writes
// nothing anywhere.
const SPEEDS = [1, 10, 60];

export default function useTelemetry({ surface, roster = [], fetchReal = null, focusSlug = null, thresholds = null, enabled = true }) {
  const sim = useCommandToggle(`sim.${surface}`, false);
  const [scenario, setScenario] = useState('calm');
  const [speed, setSpeed] = useState(1);
  const [windowKey, setWindow] = useState('24h');
  const [real, setReal] = useState(null);
  const [tick, setTick] = useState(0);
  const startedAt = useRef(Date.now());

  // Restart the virtual clock when the sim turns on or the scenario changes.
  useEffect(() => { startedAt.current = Date.now(); }, [sim.on, scenario, speed]);

  // Real feed: poll every 30s while visible.
  useEffect(() => {
    if (!enabled || sim.on || !fetchReal) return undefined;
    let dead = false;
    const pull = () => fetchReal(windowKey).then((d) => { if (!dead) setReal(d || null); }).catch(() => {});
    pull();
    const t = setInterval(() => { if (document.visibilityState !== 'hidden') pull(); }, 30e3);
    return () => { dead = true; clearInterval(t); };
  }, [enabled, sim.on, fetchReal, windowKey]);

  // Sim clock: re-render every 2s real time (the generator is ~10ms).
  useEffect(() => {
    if (!enabled || !sim.on) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 2000);
    return () => clearInterval(t);
  }, [enabled, sim.on]);

  const rosterKey = roster.map((r) => `${r.slug}|${r.pool ? 1 : 0}|${r.envRole || ''}|${r.external ? 1 : 0}|${r.twinSlug || ''}`).join(',');
  const telemetry = useMemo(() => {
    if (!enabled) return emptyTelemetry(windowKey);
    const w = WINDOWS[windowKey] || WINDOWS['24h'];
    if (sim.on) {
      const now = simClock(startedAt.current, speed);
      const src = simulate({ roster, scenario, now, windowMs: w.ms, focusSlug });
      return deriveTelemetry(src, windowKey, now, thresholds || undefined);
    }
    return deriveTelemetry(real ? { ...real, source: 'real' } : null, windowKey, Date.now(), thresholds || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sim.on, scenario, speed, windowKey, real, tick, rosterKey, focusSlug, thresholds]);

  return {
    telemetry,
    controls: { sim: sim.on, setSim: sim.set, supported: sim.supported, scenario, setScenario, speed, setSpeed, window: windowKey, setWindow },
  };
}

export function TelemetryControls({ controls, compact = false }) {
  const c = controls;
  const scn = SCENARIOS.find((s) => s.id === c.scenario);
  return (
    <div className={`cmd-telemetry ${c.sim ? 'is-sim' : ''}`}>
      <label className="cmd-switch">
        <input type="checkbox" checked={c.sim} onChange={(e) => c.setSim(e.target.checked)} />
        <span>Simulate</span>
      </label>
      {c.sim && (
        <>
          <select className="cmd-select" value={c.scenario} onChange={(e) => c.setScenario(e.target.value)} aria-label="Scenario">
            {SCENARIOS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <span className="cmd-seg" role="group" aria-label="Speed">
            {SPEEDS.map((s) => <button key={s} type="button" className={`cmd-seg-btn ${c.speed === s ? 'is-active' : ''}`} onClick={() => c.setSpeed(s)}>{s}×</button>)}
          </span>
        </>
      )}
      <span className="cmd-seg" role="group" aria-label="Window">
        {Object.values(WINDOWS).map((w) => <button key={w.key} type="button" className={`cmd-seg-btn ${c.window === w.key ? 'is-active' : ''}`} onClick={() => c.setWindow(w.key)}>{w.label}</button>)}
      </span>
      {c.sim && !compact && scn && <span className="cmd-sim-note">{scn.text}</span>}
    </div>
  );
}

// 24h activity strip for the rail: one bar per hour, peak hour marked.
export function ActivityStrip({ telemetry }) {
  const a = telemetry?.activity;
  if (!a) return null;
  const max = Math.max(1, ...a.hourly);
  const h0 = new Date(telemetry.window.now || Date.now()).getHours();
  return (
    <div className="cmd-activity" title={a.label}>
      <span className="cmd-activity-label">{a.label}</span>
      <span className="cmd-activity-bars" aria-hidden="true">
        {a.hourly.map((v, i) => (
          <i key={i} className={i === a.peakHour ? 'is-peak' : v === 0 ? 'is-zero' : ''} style={{ height: `${Math.max(2, Math.round((v / max) * 100))}%` }} />
        ))}
      </span>
      <span className="cmd-activity-axis"><span>{String((h0 + 1) % 24).padStart(2, '0')}:00</span><span>now</span></span>
    </div>
  );
}
